/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext } from 'react';
import theme from '../../../styled-components/theme.styled';

// Fields & Resources
import * as fields from '../../../configurations/fields';
import * as resources from '../../../configurations/resources';

// Hooks
import useResourceComparison from '../../../section-dashboard/hooks/widget-comparison/use-resource-comparison';
import useResourceTotalComparison from '../../../section-dashboard/hooks/widget-comparison/use-resource-total-comparison';
import useResourceSparkLineComparison from '../../../section-dashboard/hooks/widget-comparison/use-resource-spark-line-comparison';
import useResourceBigNumberComparison from '../../../section-dashboard/hooks/widget-comparison/big-number/use-resource-big-number-comparison';
import useResourcePieChartTableComparison from '../../../section-dashboard/hooks/widget-comparison/pie-chart-table/use-resource-pie-chart-table-comparison';
import useResourceTableComparison from '../../../section-dashboard/hooks/widget-comparison/table/use-resource-table-comparison';
import useResourceStackedAreaChartComparison from '../../../section-dashboard/hooks/widget-comparison/use-resource-stacked-area-chart-comparison';
import useWidgetMenuFooterQuerySelect from '../../../section-dashboard/widgets/menus/hooks/use-widget-menu-footer-query-select';
import useRequestTable from '../../../section-dashboard/hooks/request/use-request-table';
import useRequestStackedAreaChart from '../../../section-dashboard/hooks/request/use-request-stacked-area-chart';
import useResourceCombineBigNumberRequestsComparison from '../../../section-dashboard/hooks/widget-comparison/big-number/use-resource-combine-big-number-requests-comparison';
import useResourceComparisonHistogramChart from '../../../section-dashboard/hooks/widget-comparison/use-resource-comparison-histogram-chart';
import { useDatesFormatted } from '../../../hooks/use-dates';

// Widgets
import WidgetSparkLineComparison from '../../../section-dashboard/widgets/widget-comparison/widget-spark-line-comparison';
import WidgetBigNumberComparison from '../../../section-dashboard/widgets/widget-comparison/widget-big-number-comparison';
import WidgetTableComparison from '../../../section-dashboard/widgets/widget-comparison/widget-table-comparison';
import WidgetPieChartTableComparison from '../../../section-dashboard/widgets/widget-comparison/widget-pie-chart-table-comparison';
import WidgetStackedAreaChartComparison from '../../../section-dashboard/widgets/widget-comparison/widget-stacked-area-chart-comparison';
import WidgetMenuStackedAreaChart from '../../../section-dashboard/widgets/menus/components/widget-menu/widget-menu-stacked-area-chart';
import WidgetMenuTable from '../../../section-dashboard/widgets/menus/components/widget-menu/widget-menu-table';
import WidgetHistogramComparison from '../../../section-dashboard/widgets/widget-comparison/widget-histogram-chart-comparison';

// Types
import { DatePickerType } from '../../../configurations/enums/date-picker-type';
import { FilterOperator, WidgetColour, WidgetMenuObj } from '../../../section-dashboard/types';

// Queries
import { defaultGoalsDropdownQuery, goalsDropdownQuery } from '../../../section-dashboard/queries';

// Components
import BeforeGridConfiguredGoals from '../../../section-dashboard/components/sections/before-grid/before-grid-configured-goals';
import BeforeSectionContentUniverseFilter from '../../components/sections/before-section/before-section-content-universe-filter';
import useResourcePieChartTableLimitComparison from '../../../section-dashboard/hooks/widget-comparison/pie-chart-table/use-resource-pie-chart-table-limit-comparison';

// Context
import { ContentDetailDashboardMenuContext } from '../../content-detail-context/content-detail-dashboard-menu-context';

export const config = {
    title: 'Content Detail Dashboard',
    description: '', // TODO description
    filters: {
        status: {
            isEnableDatePicker: true,
            isEnableComparisonDatePicker: true,
            isEnableProductSelect: false,
            isEnableMetrics: false,
            isEnableButtonGroup: false,
            isEnableChannelMultiSelect: false,
            isEnableChannelSingleSelect: {
                enabled: false,
            },
            isEnablePdfExport: true,
        },
        comparisonStatus: {
            showComparisonDatePicker: true,
            redirectUrl: 'content-detail-dashboard',
        },
        datePicker: {},
        comparisonDatePicker: {
            datePickerType: DatePickerType.Comparison,
        },
    },
    context: () => {
        // Context
        const menuContext = useContext(ContentDetailDashboardMenuContext);
        return { menuContext };
    },
    layouts: {
        xs: {
            sections: [
                {
                    title: 'Overall Profile',
                    grid: `
                    'a'
                    'b'
                    'c'
                    'd'
                    `,
                },
                {
                    title: 'Goals Snapshot',
                    beforeGrid: <BeforeGridConfiguredGoals />,
                    collapsed: true,
                    grid: `
                    'e'
                    'f'
                    'g'
                    'h'
                    'h'
                    'i'
                    'j'
                    'k'
                    'l'
                    `,
                },
                {
                    title: 'Goal Completions Over Time',
                    collapsed: true,
                    grid: `
                    'm'
                    `,
                },
                {
                    title: 'Traffic Overview',
                    beforeSection: <BeforeSectionContentUniverseFilter />,
                    grid: `
                    'n'
                    'o'
                    'p'
                    'q'
                    `,
                },
                {
                    title: 'Goal Overview',
                    menu: (): JSX.Element => config.context().menuContext.goalsMenu.menu,
                    grid: `
                    'r'
                    's'
                    't'
                    'u'
                    `,
                },
                {
                    title: 'Goals Overview - Normalised Values (Deltas)',
                    menu: (): JSX.Element => config.context().menuContext.goalOverviewNormalisedValuesMenu.menu,
                    grid: `
                    'v'
                    `,
                },
            ],
        },
        sm: {
            sections: [
                {
                    title: 'Overall Profile',
                    grid: `
                    'a b'
                    'c d'
                    `,
                },
                {
                    title: 'Goals Snapshot',
                    beforeGrid: <BeforeGridConfiguredGoals />,
                    collapsed: true,
                    grid: `
                    'e f'
                    'g g'
                    'h h'
                    'h h'
                    'i i'
                    'i i'
                    'j j'
                    'k k'
                    'l l'
                    `,
                },
                {
                    title: 'Goal Completions Over Time',
                    collapsed: true,
                    grid: `
                    'm m'
                    `,
                },
                {
                    title: 'Traffic Overview',
                    beforeSection: <BeforeSectionContentUniverseFilter />,
                    grid: `
                    'n n'
                    'o p'
                    'q q'
                    `,
                },
                {
                    title: 'Goal Overview',
                    menu: (): JSX.Element => config.context().menuContext.goalsMenu.menu,
                    grid: `
                    'r r'
                    's t'
                    'u u'
                    `,
                },
                {
                    title: 'Goals Overview - Normalised Values (Deltas)',
                    menu: (): JSX.Element => config.context().menuContext.goalOverviewNormalisedValuesMenu.menu,
                    grid: `
                    'v v'
                    `,
                },
            ],
        },
        md: {
            sections: [
                {
                    title: 'Overall Profile',
                    grid: `
                    'a a b b'
                    'c c d d'
                    `,
                },
                {
                    title: 'Goals Snapshot',
                    beforeGrid: <BeforeGridConfiguredGoals />,
                    collapsed: true,
                    grid: `
                    'i h h e'
                    'i h h g'
                    'j f f k'
                    'l l l l'
                    `,
                },
                {
                    title: 'Goal Completions Over Time',
                    collapsed: true,
                    grid: `
                    'm m m m'
                    'm m m m'
                    `,
                },
                {
                    title: 'Traffic Overview',
                    beforeSection: <BeforeSectionContentUniverseFilter />,
                    grid: `
                    'n n n n'
                    'n n n n'
                    'o o p p'
                    'q q q q'
                    'q q q q'
                    `,
                },
                {
                    title: 'Goal Overview',
                    menu: (): JSX.Element => config.context().menuContext.goalsMenu.menu,
                    grid: `
                    'r r r r'
                    'r r r r'
                    's s t t'
                    'u u u u'
                    'u u u u'
                    `,
                },
                {
                    title: 'Goals Overview - Normalised Values (Deltas)',
                    menu: (): JSX.Element => config.context().menuContext.goalOverviewNormalisedValuesMenu.menu,
                    grid: `
                    'v v v v'
                    'v v v v'
                    `,
                },
            ],
        },
        lg: {
            sections: [
                {
                    title: 'Overall Profile',
                    grid: `
                    'a a a b b b'
                    'a a a b b b'
                    'c c c d d d'
                    'c c c d d d'
                    `,
                },
                {
                    title: 'Goals Snapshot',
                    beforeGrid: <BeforeGridConfiguredGoals />,
                    collapsed: true,
                    grid: `
                    'i i h h e j'
                    'i i h h f k'
                    'i i h h g l'
                    `,
                },
                {
                    title: 'Goal Completions Over Time',
                    collapsed: true,
                    grid: `
                    'm m m m m m'
                    'm m m m m m'
                    `,
                },
                {
                    title: 'Traffic Overview',
                    beforeSection: <BeforeSectionContentUniverseFilter />,
                    grid: `
                    'n n n n n n'
                    'n n n n n n'
                    'n n n n n n'
                    'o o o p p p'
                    'q q q q q q'
                    'q q q q q q'
                    'q q q q q q'
                    `,
                },
                {
                    title: 'Goal Overview',
                    menu: (): JSX.Element => config.context().menuContext.goalsMenu.menu,
                    grid: `
                    'r r r r r r'
                    'r r r r r r'
                    'r r r r r r'
                    's s s t t t'
                    'u u u u u u'
                    'u u u u u u'
                    'u u u u u u'
                    `,
                },
                {
                    title: 'Goals Overview - Normalised Values (Deltas)',
                    menu: (): JSX.Element => config.context().menuContext.goalOverviewNormalisedValuesMenu.menu,
                    grid: `
                    'v v v v v v'
                    'v v v v v v'
                    'v v v v v v'
                    `,
                },
            ],
        },
        xl: {
            sections: [
                {
                    title: 'Overall Profile',
                    grid: `
                    'a a b b c c d d'
                    'a a b b c c d d'
                    `,
                },
                {
                    title: 'Goals Snapshot',
                    beforeGrid: <BeforeGridConfiguredGoals />,
                    collapsed: true,
                    grid: `
                    'i i h h h h e j'
                    'i i h h h h f k'
                    'i i h h h h g l'
                    `,
                },
                {
                    title: 'Goal Completions Over Time',
                    collapsed: true,
                    grid: `
                    'm m m m m m m m'
                    'm m m m m m m m'
                    'm m m m m m m m'
                    `,
                },
                {
                    title: 'Traffic Overview',
                    beforeSection: <BeforeSectionContentUniverseFilter />,
                    grid: `
                    'n n n n n n n n'
                    'n n n n n n n n'
                    'n n n n n n n n'
                    'o o o o p p p p'
                    'o o o o p p p p'
                    'q q q q q q q q'
                    'q q q q q q q q'
                    'q q q q q q q q'
                    `,
                },
                {
                    title: 'Goal Overview',
                    menu: (): JSX.Element => config.context().menuContext.goalsMenu.menu,
                    grid: `
                    'r r r r r r r r'
                    'r r r r r r r r'
                    'r r r r r r r r'
                    's s s s t t t t'
                    's s s s t t t t'
                    'u u u u u u u u'
                    'u u u u u u u u'
                    'u u u u u u u u'
                    `,
                },
                {
                    title: 'Goals Overview - Normalised Values (Deltas)',
                    menu: (): JSX.Element => config.context().menuContext.goalOverviewNormalisedValuesMenu.menu,
                    grid: `
                    'v v v v v v v v'
                    'v v v v v v v v'
                    'v v v v v v v v'
                    `,
                },
            ],
        },
    },
    components: () => {
        const { endDate } = useDatesFormatted({ resource: resources.REPORT_CONTENT_DASHBOARD_PATH });

        const {
            fieldToView,
            tierTwoMenuCombinedRequests,
            tierTwoAndGoalsMenuRequestMerge,
            goalOverviewNormalisedValuesMenu,
        } = useContext(ContentDetailDashboardMenuContext);

        /***
         Title: Overall Profile
         Widget: Big Numbers
        */
        // Sparklines
        const goalSnapshotData = useResourceComparison({
            resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_SNAPSHOT,
            request: {
                isGraph: true,
                groupBy: fields.SALES_DATE_DATE,
                graphGroupBy: [fields.SALES_DATE_DATE],
                graphMetrics: [
                    fields.VISITS,
                    fields.COST,
                    fields.FM_SALES,
                    fields.FM_REVENUE,
                    fields.GOAL_CVR_GOAL_SNAPSHOT,
                ],
            },
        });

        const sparkTotalData = useResourceComparison({
            resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_SNAPSHOT,
            request: {
                groupBy: fields.SALES_DATE_DATE,
                orderBy: [{ field: fields.SALES_DATE_DATE, orderByDirection: 'asc' }],
                totals: true,
            },
        });

        const sparkTotals = useResourceTotalComparison({
            resourceData: sparkTotalData,
        });

        // Visits Sparkline
        const visitsSpark = useResourceSparkLineComparison({
            resourceData: goalSnapshotData,
            metric: fields.VISITS,
            dateDimension: fields.SALES_DATE_DATE,
        });

        // Sales Sparkline
        const salesSpark = useResourceSparkLineComparison({
            resourceData: goalSnapshotData,
            metric: fields.FM_SALES,
            dateDimension: fields.SALES_DATE_DATE,
        });

        // Revenue Sparkline
        const revenueSpark = useResourceSparkLineComparison({
            resourceData: goalSnapshotData,
            metric: fields.FM_REVENUE,
            dateDimension: fields.SALES_DATE_DATE,
        });

        // Cost Sparkline
        const costSpark = useResourceSparkLineComparison({
            resourceData: goalSnapshotData,
            metric: fields.COST,
            dateDimension: fields.SALES_DATE_DATE,
        });

        /***
         Title: Goal Snapshot
         Widget: Big Numbers, Pie Chart, Table
        */

        const goalSnapshotDefaultGoalTotalsData = (menu: WidgetMenuObj) =>
            useResourceComparison({
                resource: resources.REPORT_MCS_CHANNEL,
                request: {
                    isGraph: true,
                    groupBy: fields.PRODUCT,
                    graphGroupBy: [fields.PRODUCT],
                    graphMetrics: [fields.FM_SALES, fields.FM_CVR, fields.FM_REVENUE],
                    ...(menu.request.filters ?? []),
                    filters: [
                        ...(menu.request.filters ?? []),
                        { field: fields.PRODUCT_DEFAULT, operator: FilterOperator.EQUALS, value: '1' },
                    ],
                    selectedFields: [
                        fields.PRODUCT,
                        fields.SALES_DATE_DATETIME,
                        fields.FM_SALES,
                        fields.FM_CVR,
                        fields.FM_REVENUE,
                    ],
                },
            });

        // Default Goal Goals Completed
        const defaultGoalTotalGoalsBigNumberMenu = useWidgetMenuFooterQuerySelect({
            query: defaultGoalsDropdownQuery,
            dropdownField: fields.PRODUCT,
            includeAllOption: true,
        });
        const defaultGoalTotalGoalsBigNumber = useResourceBigNumberComparison({
            resourceData: goalSnapshotDefaultGoalTotalsData(defaultGoalTotalGoalsBigNumberMenu),
            field: fields.FM_SALES,
            aggregation: 'sum',
            displayGraph: false,
        });

        // Default Goal Attributed Value
        const defaultGoalTotalRevenueBigNumberMenu = useWidgetMenuFooterQuerySelect({
            query: defaultGoalsDropdownQuery,
            dropdownField: fields.PRODUCT,
            includeAllOption: true,
        });
        const defaultGoalTotalRevenueBigNumber = useResourceBigNumberComparison({
            resourceData: goalSnapshotDefaultGoalTotalsData(defaultGoalTotalRevenueBigNumberMenu),
            field: fields.FM_REVENUE,
            aggregation: 'sum',
            formatting: 'currency',
            displayGraph: false,
        });

        // Default Goal CR%
        const defaultGoalCRBigNumberMenu = useWidgetMenuFooterQuerySelect({
            query: defaultGoalsDropdownQuery,
            dropdownField: fields.PRODUCT,
            includeAllOption: true,
        });
        const defaultGoalCRBigNumber = useResourceBigNumberComparison({
            resourceData: goalSnapshotDefaultGoalTotalsData(defaultGoalCRBigNumberMenu),
            field: fields.FM_CVR,
            aggregation: 'sum',
            formatting: 'percentage',
            rounding: {
                applyRounding: true,
                decimalPoints: 2,
            },
            displayGraph: false,
        });

        const goalSnapshotBigNumberData = (menu: WidgetMenuObj) =>
            useResourceComparison({
                resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_SNAPSHOT,
                request: {
                    isGraph: true,
                    groupBy: fields.SALES_DATE_DATETIME,
                    graphGroupBy: [fields.SALES_DATE_DATETIME],
                    graphMetrics: [
                        fields.VISITS,
                        fields.COST,
                        fields.FM_SALES,
                        fields.FM_REVENUE,
                        fields.GOAL_CVR_GOAL_SNAPSHOT,
                    ],
                    ...menu.request,
                    selectedFields: [
                        fields.SALES_DATE_DATE,
                        fields.VISITS,
                        fields.COST,
                        fields.FM_SALES,
                        fields.FM_REVENUE,
                        fields.GOAL_CVR_GOAL_SNAPSHOT,
                    ],
                },
            });

        // Toal Goals Completed
        const totalGoalsCompletedBigNumberMenu = useWidgetMenuFooterQuerySelect({
            query: goalsDropdownQuery,
            dropdownField: fields.PRODUCT,
            includeAllOption: true,
        });
        const totalGoalsCompletedBigNumber = useResourceBigNumberComparison({
            resourceData: goalSnapshotBigNumberData(totalGoalsCompletedBigNumberMenu),
            field: fields.FM_SALES,
            aggregation: 'sum',
            displayGraph: false,
        });

        // Total Attributed Value
        const totalAttributedValueBigNumberMenu = useWidgetMenuFooterQuerySelect({
            query: goalsDropdownQuery,
            dropdownField: fields.PRODUCT,
            includeAllOption: true,
        });
        const totalAttributedValueBigNumber = useResourceBigNumberComparison({
            resourceData: goalSnapshotBigNumberData(totalAttributedValueBigNumberMenu),
            field: fields.FM_REVENUE,
            aggregation: 'sum',
            formatting: 'currency',
            displayGraph: false,
        });

        // Total Goals CR%
        const totalCVRBigNumberMenu = useWidgetMenuFooterQuerySelect({
            query: goalsDropdownQuery,
            dropdownField: fields.PRODUCT,
            includeAllOption: true,
        });
        const totalCVRBigNumber = useResourceBigNumberComparison({
            resourceData: goalSnapshotBigNumberData(totalCVRBigNumberMenu),
            field: fields.GOAL_CVR_GOAL_SNAPSHOT,
            aggregation: 'sum',
            formatting: 'percentage',
            rounding: {
                applyRounding: true,
                decimalPoints: 2,
            },
            displayGraph: false,
        });

        // Goals Snapshot Pie
        const goalSnapshotGoalsPieData = useResourceComparison({
            resource: resources.REPORT_MCS_CHANNEL,
            request: {
                groupBy: fields.PRODUCT,
                orderBy: [{ field: fields.FM_SALES, orderByDirection: 'desc' }],
                chartField: fields.FM_SALES,
                selectedFields: [fields.PRODUCT, fields.FM_SALES],
            },
        });

        let goalSnapshotPie = useResourcePieChartTableComparison({
            resourceData: goalSnapshotGoalsPieData,
            chartDimension: fields.PRODUCT,
        });

        // Goal Snapshot Table
        const goalSnapshotRequest = useRequestTable({
            groupBy: fields.PRODUCT,
            orderBy: [{ field: fields.GOAL_COMPLETIONS, orderByDirection: 'desc' }],
            fields: [fields.PRODUCT, fields.GOAL_COMPLETIONS, fields.GOAL_VALUE, fields.GOAL_CVR_GOAL_SNAPSHOT],
            selectedFields: [fields.PRODUCT, fields.GOAL_COMPLETIONS, fields.GOAL_VALUE, fields.GOAL_CVR_GOAL_SNAPSHOT],
        });

        const goalSnapshotTableData = useResourceComparison({
            resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_SNAPSHOT,
            request: goalSnapshotRequest,
        });

        const goalSnapshotTotalData = useResourceComparison({
            resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_SNAPSHOT,
            request: {
                ...goalSnapshotRequest,
                totals: true,
            },
        });

        const goalSnapshotTable = useResourceTableComparison({
            resourceData: goalSnapshotTableData,
            request: goalSnapshotRequest,
            dimension: fields.PRODUCT,
        });

        const goalSnapshotTableTotals = useResourceTotalComparison({
            resourceData: goalSnapshotTotalData,
        });

        /***
         Title: Goal Completions
         Widget: Stacked Area Chart
        */
        const goalCompletionStackedAreaChartRequest = useRequestStackedAreaChart({
            yAxisOptions: [
                { name: 'Count of Completions', field: fields.FM_SALES, active: true },
                { name: 'Value of Completions', field: fields.FM_REVENUE, active: false },
            ],
            groupBy: fields.PRODUCT,
            orderBy: [
                { field: fields.SALES_DATE_DATE, orderByDirection: 'asc' },
                { field: fields.GOAL_COMPLETIONS, orderByDirection: 'desc' },
            ],
            selectedFields: [fields.SALES_DATE_DATE, fields.GOAL_COMPLETIONS, fields.FM_SALES, fields.FM_REVENUE],
        });

        const goalCompletionsData = useResourceComparison({
            resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_COMPLETION,
            request: goalCompletionStackedAreaChartRequest,
        });

        const goalCompletionsAdditionalSeries = useResourceComparison({
            resource: resources.REPORT_CHANNEL_DASHBOARD_GOAL_COMPLETION,
            request: {
                groupBy: fields.SALES_DATE_DATE,
                orderBy: [{ field: fields.SALES_DATE_DATE, orderByDirection: 'asc' }],
            },
        });

        const goalCompletionStackedAreaChart = useResourceStackedAreaChartComparison({
            areaSeriesMetric: fields.PRODUCT,
            resourceData: goalCompletionsData,
            dateDimension: fields.SALES_DATE_DATE,
            optionalSeries: [
                {
                    title: 'Unique Visits',
                    yAxis: fields.VISITS,
                    resourceData: goalCompletionsAdditionalSeries,
                    colour: theme.sectionDashboard.colour.purple,
                },
                {
                    title: 'Unique Visitors',
                    yAxis: fields.VISITORS,
                    resourceData: goalCompletionsAdditionalSeries,
                    colour: theme.sectionDashboard.colour.emerald,
                },
            ],
        });

        /***
         Title: Traffic Overview
         Widget: Pie Chart Table
        */
        const trafficOverviewOfUniquePageviewRequest = {
            groupBy: fieldToView.field,
            orderBy: [{ field: fields.UNIQUE_PAGE_VIEWS, orderByDirection: 'desc' }],
            fields: [fieldToView.field, fields.UNIQUE_PAGE_VIEWS],
            chartField: fields.UNIQUE_PAGE_VIEWS,
            ...tierTwoMenuCombinedRequests.request,
        };

        const trafficOverviewOfUniquePageviewData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: trafficOverviewOfUniquePageviewRequest,
        });

        const trafficOverviewOfUniquePageviewTotalsData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: {
                ...trafficOverviewOfUniquePageviewRequest,
                totals: true,
            },
        });

        const trafficOverviewOfUniquePageviewDonutPieChartTableTotal = useResourceTotalComparison({
            resourceData: trafficOverviewOfUniquePageviewTotalsData,
        });

        const trafficOverviewOfUniquePageviewDonutPieChartTable = useResourcePieChartTableComparison({
            resourceData: trafficOverviewOfUniquePageviewData,
            chartDimension: fieldToView.field,
        });

        const trafficOverviewOfUniquePageviewDonutPieChartTableLimit = useResourcePieChartTableLimitComparison({
            resourceData: trafficOverviewOfUniquePageviewDonutPieChartTable,
            totalData: trafficOverviewOfUniquePageviewDonutPieChartTableTotal,
            ratioField: fields.UNIQUE_PAGE_VIEWS,
            limit: 10,
        });

        /***
         Title: Traffic Overview
         Widget: Big Number
        */
        //  Total Count of Indexed Pages Resource
        const totalCountOfIndexedPagesTotalsData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                ignoreDate: true,
                isGraph: true,
                graphGroupBy: [fields.PATH_PATH],
                graphMetrics: [fields.INDEXED_PAGE_COUNT],
                filters: [
                    {
                        field: fields.CREATED_DATETIME,
                        operator: FilterOperator.EQUALS,
                        value: endDate,
                    },
                ],
                ...tierTwoMenuCombinedRequests.request,
            },
        });

        // Count of Pages Conversion Resource
        const totalCountOfPagesConversionTotalsData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                ignoreDate: true,
                isGraph: true,
                graphGroupBy: [fields.PATH_PATH],
                graphMetrics: [fields.PAGES_CONVERSION_COUNT],
                filters: [
                    {
                        field: fields.CREATED_DATETIME,
                        operator: FilterOperator.EQUALS,
                        value: endDate,
                    },
                ],
                ...tierTwoMenuCombinedRequests.request,
            },
        });

        // Home Page Resource
        const homepageHomeRelianceAnalysisTrafficData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                isGraph: true,
                graphGroupBy: [fields.PATH_SUBDIRECTORY],
                graphMetrics: [fields.PAGES_VIEWED_COUNT],
                filters: [{ field: fields.PATH_SUBDIRECTORY, operator: FilterOperator.EQUALS, value: 'home' }],
            },
        });

        // '/' Root page Resource
        const homepageRootRelianceAnalysisTrafficData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                isGraph: true,
                graphGroupBy: [fields.PATH_PATH],
                graphMetrics: [fields.PAGES_VIEWED_COUNT],
                filters: [{ field: fields.PATH_PATH, operator: FilterOperator.EQUALS, value: '/' }],
            },
        });

        // Combine the Home and '/' Root page resources
        const totalCountOfHomePageViewedTotalsData = useResourceCombineBigNumberRequestsComparison({
            resourceData: [homepageHomeRelianceAnalysisTrafficData, homepageRootRelianceAnalysisTrafficData],
        });

        // All Page Resource
        const totalCountOfAllPagesTotalsData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                isGraph: true,
                graphGroupBy: [fields.PATH_SUBDIRECTORY],
                graphMetrics: [fields.PAGES_VIEWED_COUNT],
            },
        });

        // Count of Pages with zero Page Views
        const countOfPagesWithZeroPageViewsBigNumber = useResourceBigNumberComparison({
            resourceData: totalCountOfIndexedPagesTotalsData,
            aggregationSumData: totalCountOfPagesConversionTotalsData,
            field: fields.INDEXED_PAGE_COUNT,
            aggregationSumField: fields.PAGES_CONVERSION_COUNT,
            aggregation: 'difference',
            displayGraph: false,
        });

        // Homepage reliance %
        const homePageReliancePercentageBigNumber = useResourceBigNumberComparison({
            resourceData: totalCountOfHomePageViewedTotalsData,
            aggregationSumData: totalCountOfAllPagesTotalsData,
            field: fields.PAGES_VIEWED_COUNT,
            aggregationSumField: fields.PAGES_VIEWED_COUNT,
            aggregation: 'ratio',
            formatting: 'percentage',
            rounding: {
                applyRounding: true,
                decimalPoints: 0,
            },
            displayGraph: false,
        });

        /***
         Title: Traffic Overview
         Widget: Table
        */
        const trafficOverviewByPageTypeRequest = useRequestTable({
            groupBy: fieldToView.field,
            fields: [
                fieldToView.field,
                fields.UNIQUE_PAGE_VIEWS,
                fields.PAGE_VIEWS,
                fields.PAGE_EXITS,
                fields.PAGE_EXIT_RATE,
                fields.LANDING_PAGE_VIEWS,
                fields.LANDING_PAGE_BOUNCES,
                fields.LANDING_PAGE_BOUNCE_RATE,
            ],
            orderBy: [{ field: fields.UNIQUE_PAGE_VIEWS, orderByDirection: 'desc' }],
        });

        const trafficOverviewByPageTypeData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: { ...trafficOverviewByPageTypeRequest, ...tierTwoMenuCombinedRequests.request },
        });

        const trafficOverviewByPageTypeTotalData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: {
                ...trafficOverviewByPageTypeRequest,
                totals: true,
                ...tierTwoMenuCombinedRequests.request,
            },
        });

        const trafficOverviewByPageTypeTable = useResourceTableComparison({
            resourceData: trafficOverviewByPageTypeData,
            dimension: fieldToView.field,
        });

        const trafficOverviewByPageTypeTableTotals = useResourceTotalComparison({
            resourceData: trafficOverviewByPageTypeTotalData,
        });

        /***
         Title: Goal Overview
         Widget: Pie Chart Table
        */
        const goalOverviewOfGoalCompletionCountRequest = {
            groupBy: fieldToView.field,
            orderBy: [{ field: fields.SALES, orderByDirection: 'desc' }],
            fields: [fieldToView.field, fields.SALES],
            chartField: fields.SALES,
            ...tierTwoAndGoalsMenuRequestMerge,
        };

        const goalOverviewOfGoalCompletionCountData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: { ...goalOverviewOfGoalCompletionCountRequest },
        });

        const goalOverviewOfGoalCompletionCountTotalsData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: {
                ...goalOverviewOfGoalCompletionCountRequest,
                totals: true,
            },
        });

        const goalOverviewOfGoalCompletionCountDonutPieChartTableTotal = useResourceTotalComparison({
            resourceData: goalOverviewOfGoalCompletionCountTotalsData,
        });

        const goalOverviewOfGoalCompletionCountDonutPieChartTable = useResourcePieChartTableComparison({
            resourceData: goalOverviewOfGoalCompletionCountData,
            chartDimension: fieldToView.field,
        });

        const goalOverviewOfGoalCompletionCountDonutPieChartTableLimit = useResourcePieChartTableLimitComparison({
            resourceData: goalOverviewOfGoalCompletionCountDonutPieChartTable,
            totalData: goalOverviewOfGoalCompletionCountDonutPieChartTableTotal,
            ratioField: fields.SALES,
            limit: 10,
        });

        /***
         Title: Goal Overview
         Widget: Big Number
        */
        // Total Count of Indexed Pages Resource
        const totalCountOfIndexedPagesTotalsDataForGoalCompletion = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                ignoreDate: true,
                isGraph: true,
                graphGroupBy: [fields.PATH_PATH],
                graphMetrics: [fields.INDEXED_PAGE_COUNT],
                filters: [
                    {
                        field: fields.CREATED_DATETIME,
                        operator: FilterOperator.EQUALS,
                        value: endDate,
                    },
                ],
                ...tierTwoAndGoalsMenuRequestMerge,
            },
        });

        // Count of Pages Conversion Resource
        const totalCountOfPagesConversionTotalsDataForGoalCompletion = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                ignoreDate: true,
                isGraph: true,
                graphGroupBy: [fields.PATH_PATH],
                graphMetrics: [fields.PAGES_CONVERSION_COUNT],
                filters: [
                    {
                        field: fields.CREATED_DATETIME,
                        operator: FilterOperator.EQUALS,
                        value: endDate,
                    },
                ],
                ...tierTwoAndGoalsMenuRequestMerge,
            },
        });

        // Home Page Resource
        const homepageHomeRelianceAnalysisTrafficDataForGoalCompletion = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                isGraph: true,
                graphGroupBy: [fields.PATH_SUBDIRECTORY],
                graphMetrics: [fields.PAGES_CONVERSION_COUNT],
                filters: [{ field: fields.PATH_SUBDIRECTORY, operator: FilterOperator.EQUALS, value: 'home' }],
                ...tierTwoAndGoalsMenuRequestMerge,
            },
        });

        // '/' Root page Resource
        const homepageRootRelianceAnalysisTrafficDataForGoalCompletion = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                isGraph: true,
                graphGroupBy: [fields.PATH_PATH],
                graphMetrics: [fields.PAGES_CONVERSION_COUNT],
                filters: [{ field: fields.PATH_PATH, operator: FilterOperator.EQUALS, value: '/' }],
                ...tierTwoAndGoalsMenuRequestMerge,
            },
        });

        // Combine the Home and '/' Root page resources
        const totalCountOfHomePageViewedTotalsDataForGoalCompletion = useResourceCombineBigNumberRequestsComparison({
            resourceData: [
                homepageHomeRelianceAnalysisTrafficDataForGoalCompletion,
                homepageRootRelianceAnalysisTrafficDataForGoalCompletion,
            ],
        });

        // All Page Resource
        const totalCountOfAllPagesTotalsDataForGoalCompletion = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH_PERFORMANCE,
            request: {
                isGraph: true,
                graphGroupBy: [fields.PATH_SUBDIRECTORY],
                graphMetrics: [fields.PAGES_CONVERSION_COUNT],
            },
        });

        // Count of Pages with zero goal completions
        const countOfPagesWithZeroGoalCompletionsBigNumber = useResourceBigNumberComparison({
            resourceData: totalCountOfIndexedPagesTotalsDataForGoalCompletion,
            aggregationSumData: totalCountOfPagesConversionTotalsDataForGoalCompletion,
            field: fields.INDEXED_PAGE_COUNT,
            aggregationSumField: fields.PAGES_CONVERSION_COUNT,
            aggregation: 'difference',
            displayGraph: false,
        });

        // Homepage goal completion %
        const homePageGoalCompletionsPercentageBigNumber = useResourceBigNumberComparison({
            resourceData: totalCountOfHomePageViewedTotalsDataForGoalCompletion,
            aggregationSumData: totalCountOfAllPagesTotalsDataForGoalCompletion,
            field: fields.PAGES_CONVERSION_COUNT,
            aggregationSumField: fields.PAGES_CONVERSION_COUNT,
            aggregation: 'ratio',
            formatting: 'percentage',
            rounding: {
                applyRounding: true,
                decimalPoints: 0,
            },
            displayGraph: false,
        });

        /***
         Title: Goal Overview
         Widget: Table
        */
        const goalOverviewByPageTypeRequest = useRequestTable({
            groupBy: fieldToView.field,
            fields: [
                fieldToView.field,
                fields.UNIQUE_PAGE_VIEWS,
                fields.PAGE_VIEWS,
                fields.PAGE_EXITS,
                fields.PAGE_EXIT_RATE,
                fields.LANDING_PAGE_VIEWS,
                fields.LANDING_PAGE_BOUNCES,
                fields.LANDING_PAGE_BOUNCE_RATE,
                fields.SALES,
                fields.REVENUE,
                fields.ASSISTS,
                fields.ASSISTS_PAGE_VIEWS_RATE,
                fields.CONVERSIONS,
                fields.CONVERSIONS_CVR,
                fields.CONVERSION_VALUE,
            ],
            orderBy: [{ field: fields.UNIQUE_PAGE_VIEWS, orderByDirection: 'desc' }],
        });

        const goalOverviewByPageTypeData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: { ...goalOverviewByPageTypeRequest, ...tierTwoAndGoalsMenuRequestMerge },
        });

        const goalOverviewByPageTypeTotalData = useResourceComparison({
            resource: resources.REPORT_CONTENT_DASHBOARD_PATH,
            request: {
                ...goalOverviewByPageTypeRequest,
                totals: true,
                ...tierTwoAndGoalsMenuRequestMerge,
            },
        });

        const goalOverviewByPageTypeTable = useResourceTableComparison({
            resourceData: goalOverviewByPageTypeData,
            dimension: fieldToView.field,
        });

        const goalOverviewByPageTypeTableTotals = useResourceTotalComparison({
            resourceData: goalOverviewByPageTypeTotalData,
        });

        /***
         Title: Goals Overview: Normalised Values (Deltas)
         Widget: Histogram Chart
        */
        const goalOverviewNormalizedValueRequest = {
            groupBy: fields.SALES_DATE_DATE,
            orderBy: [{ field: fields.ATTRIBUTED_VALUE, orderByDirection: 'desc' }],
            fields: [fields.PRODUCT, fields.SALES_DATE_DATE],
            chartField: fields.SALES_DATE_DATE,
            ...goalOverviewNormalisedValuesMenu.request,
        };

        const goalOverviewNormalizedValueResourceData = useResourceComparison({
            resource: resources.REPORT_PAGE_ATTRIBUTION_VALUE,
            request: { ...goalOverviewNormalizedValueRequest },
        });

        const goalOverviewNormalizedValueHistogramData = useResourceComparisonHistogramChart({
            resourceData: goalOverviewNormalizedValueResourceData,
            category: fields.PRODUCT,
            metric: fields.ATTRIBUTED_VALUE,
        });

        return {
            a: (
                <WidgetSparkLineComparison
                    title="Visits by Day"
                    data={visitsSpark}
                    totalData={sparkTotals}
                    colour={WidgetColour.Teal}
                />
            ),
            b: (
                <WidgetSparkLineComparison
                    title="Sales by Day"
                    data={salesSpark}
                    totalData={sparkTotals}
                    colour={WidgetColour.Orange}
                />
            ),
            c: (
                <WidgetSparkLineComparison
                    title="Revenue by Day"
                    data={revenueSpark}
                    totalData={sparkTotals}
                    colour={WidgetColour.Green}
                />
            ),
            d: (
                <WidgetSparkLineComparison
                    title="Cost by Day"
                    data={costSpark}
                    totalData={sparkTotals}
                    colour={WidgetColour.Rose}
                />
            ),
            e: (
                <WidgetBigNumberComparison
                    title="Default Goal Goals Completed"
                    data={defaultGoalTotalGoalsBigNumber}
                    menu={defaultGoalTotalGoalsBigNumberMenu.menu}
                />
            ),
            f: (
                <WidgetBigNumberComparison
                    title="Default Goal Attributed Value"
                    data={defaultGoalTotalRevenueBigNumber}
                    menu={defaultGoalTotalRevenueBigNumberMenu.menu}
                />
            ),
            g: (
                <WidgetBigNumberComparison
                    title="Default Goal CR%"
                    data={defaultGoalCRBigNumber}
                    menu={defaultGoalCRBigNumberMenu.menu}
                />
            ),
            h: (
                <WidgetTableComparison
                    title="Goals Snapshot"
                    data={goalSnapshotTable}
                    totalData={goalSnapshotTableTotals}
                    menu={<WidgetMenuTable data={goalSnapshotTable} />}
                />
            ),
            i: <WidgetPieChartTableComparison title={'Goals Completed'} data={goalSnapshotPie} display="donut" />,
            j: (
                <WidgetBigNumberComparison
                    title="Total Goals Completed"
                    data={totalGoalsCompletedBigNumber}
                    menu={totalGoalsCompletedBigNumberMenu.menu}
                />
            ),
            k: (
                <WidgetBigNumberComparison
                    title="Total Attributed Value"
                    data={totalAttributedValueBigNumber}
                    menu={totalAttributedValueBigNumberMenu.menu}
                />
            ),
            l: (
                <WidgetBigNumberComparison
                    title="All Goals CR%"
                    data={totalCVRBigNumber}
                    menu={totalCVRBigNumberMenu.menu}
                />
            ),
            m: (
                <WidgetStackedAreaChartComparison
                    title="Goal Completions Over Time"
                    data={goalCompletionStackedAreaChart}
                    menu={
                        <WidgetMenuStackedAreaChart
                            data={goalCompletionStackedAreaChart}
                            request={goalCompletionStackedAreaChartRequest}
                        />
                    }
                />
            ),
            n: (
                <WidgetPieChartTableComparison
                    title="Traffic Overview"
                    data={trafficOverviewOfUniquePageviewDonutPieChartTableLimit}
                    totalData={trafficOverviewOfUniquePageviewDonutPieChartTableTotal}
                    href=""
                    display="donut"
                />
            ),
            o: (
                <WidgetBigNumberComparison
                    title="Count of Pages with Zero Page Views"
                    data={countOfPagesWithZeroPageViewsBigNumber}
                />
            ),
            p: <WidgetBigNumberComparison title="Homepage Reliance %" data={homePageReliancePercentageBigNumber} />,
            q: (
                <WidgetTableComparison
                    title="Traffic Overview"
                    data={trafficOverviewByPageTypeTable}
                    totalData={trafficOverviewByPageTypeTableTotals}
                    href=""
                    menu={<WidgetMenuTable data={trafficOverviewByPageTypeTable} />}
                />
            ),
            r: (
                <WidgetPieChartTableComparison
                    title="Goal Overview"
                    data={goalOverviewOfGoalCompletionCountDonutPieChartTableLimit}
                    totalData={goalOverviewOfGoalCompletionCountDonutPieChartTableTotal}
                    href=""
                    display="donut"
                />
            ),
            s: (
                <WidgetBigNumberComparison
                    title="Count of Pages with Zero Goal Completions"
                    data={countOfPagesWithZeroGoalCompletionsBigNumber}
                />
            ),
            t: (
                <WidgetBigNumberComparison
                    title="Homepage Goal Completion %"
                    data={homePageGoalCompletionsPercentageBigNumber}
                />
            ),
            u: (
                <WidgetTableComparison
                    title="Goal Overview"
                    data={goalOverviewByPageTypeTable}
                    totalData={goalOverviewByPageTypeTableTotals}
                    href=""
                    menu={<WidgetMenuTable data={goalOverviewByPageTypeTable} />}
                />
            ),
            v: (
                <WidgetHistogramComparison
                    title="Goals Overview - Normalised Values (Deltas)"
                    data={goalOverviewNormalizedValueHistogramData}
                    href=""
                />
            ),
        };
    },
};
